import {isEqual} from 'lodash';
import React, {FC, ReactNode, ReactText, useEffect, useState} from 'react';
import styled from 'styled-components';

import {defineMessage, Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {usePrevious, useToggle} from '../../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {newPalette, NewPaletteColorsEnum} from '../../../../../libs/shared/ui/src/styles/colorStyles';
import {flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {PaletteColorsEnum} from '../../../../../libs/shared/ui/src/styles/paletteModel';
import {useLocalize} from '../../../../../srcCommon/internationalization/internationalizationContext';
import {useAppDispatch, useAppSelector} from '../../../../helpers/reduxHelpers';
import {
  AnalyticsDataPointTypesEnum,
  AnalyticsDonutMetricValue,
  AnalyticsGraphMetricsEnum,
  AnalyticsGraphMetricValue,
  AnalyticsScalarMetricsEnum,
  AnalyticsTableMetricsEnum,
} from '../../../../models/api/analyticsMetricsModel';
import {
  AnalyticsOptionsBucketBoundaries,
  AnalyticsSelectedMetricsModel,
} from '../../../../models/api/analyticsSavedViewModel';
import {InterfaceEventsEnum} from '../../../../services/tracking';
import {getHasAnalyticsResolutionMetrics} from '../../../../store/featureFlags/featureFlagsSelectors';
import {setMetricDetailsAction} from '../../../../store/panels/analytics/details/analyticsPanelDetailsActions';
import {
  getAnalyticsSecondaryMetricIsLoading,
  getAnalyticsSelectedMetrics,
} from '../../../../store/panels/analytics/report/analyticsPanelReportSelectors';
import {
  AnalyticsReportsEnum,
  BasicReportProps,
  ContributingSegmentsOptionsEnum,
  isComputedGraphMetric,
  overviewGraphResolutionMetrics,
  ReportElementIdsEnum,
  ReportFilterOptionsEnum,
} from '../../../helpers/analyticsReportHelpers';
import {useAnalyticsUpdateSelectedMetrics} from '../../../helpers/hooks/useAnalyticsUpdateSelectedMetrics';
import {getMetricLabels} from '../../../helpers/metricLabels';
import {useAnalyticsTracking} from '../../../helpers/tracking/useAnalyticsTracking';
import {
  AnalyticsComputedGraphMetricsEnum,
  AnalyticsComputedProgressMetricsEnum,
} from '../../../transformers/analyticsMetricsTransformersTypes';
import {ChartSerie} from '../../charts/commons/types';
import {makeExportTitle} from '../../exports/helpers/metricExports';
import {ReportDonutChart} from '../../reportElements/charts/reportDonutChart';
import {ReportLineChart} from '../../reportElements/charts/reportLineChart';
import {ReportProgressBar} from '../../reportElements/charts/reportProgressBar';
import {
  HistoMetricRangeTypesEnum,
  HistoMetricSelection,
  HistoMetricSelectionDropdown,
} from '../../reportElements/elementSettings/histoMetricSelectionDropdown';
import {HistoSettingsBucketsPicker} from '../../reportElements/elementSettings/histoSettingsBucketsPicker/histoSettingsBucketsPicker';
import {AnyGraphMetricsEnum} from '../../reportElements/helperTypes';
import {ReportHeroCards} from '../../reportElements/heroCards/reportHeroCards';
import {ReportList} from '../../reportElements/list/reportList';
import {ReportElementWrapper} from '../../reportElements/reportElement';
import {AnalyticsTooltip} from '../../reportElements/tooltips/analyticsTooltips';
import {topListsFilter} from './topListsFilter';

/*
 * Constants.
 */

const perfGraphSeries: Array<ChartSerie<AnalyticsGraphMetricValue>> = [
  {
    accessor: (d) => d.value,
    label: '', // Label will be set dynamically based on the selected metric.
    color: newPalette[NewPaletteColorsEnum.GREEN].regular,
  },
];

enum OverviewListsLabelAccessorsEnum {
  REPLY_TIME = 'time',
  TEAMMATE = 'teammate_id',
  TAGS = 'tag',
}

const progressBarLegendRenderers = [
  (val: number) => (
    <StyledLegendItemDiv>
      <Trans id="overview-pct-unresolved-legend-label">{val}% unresolved</Trans>
      <AnalyticsTooltip
        tooltip={getMetricLabels(AnalyticsScalarMetricsEnum.NUM_UNRESOLVED_ACTIVE_SEGMENTS).tooltip}
      />
    </StyledLegendItemDiv>
  ),
  (val: number) => (
    <StyledLegendItemDiv>
      <Trans id="overview-pct-resolved-legend-label">{val}% resolved</Trans>
      <AnalyticsTooltip tooltip={getMetricLabels(AnalyticsScalarMetricsEnum.NUM_RESOLVED_SEGMENTS).tooltip} />
    </StyledLegendItemDiv>
  ),
];

const histogramMetrics: ReadonlyArray<HistoMetricSelection> = [
  {
    donutHistoMetricId: AnalyticsComputedGraphMetricsEnum.REPLY_TIME_GRAPH,
    listHistoMetricId: AnalyticsTableMetricsEnum.REPLY_TIME_HISTOGRAM,
    innerMetricId: AnalyticsScalarMetricsEnum.AVG_RESPONSE_TIME,
    innerMetricType: AnalyticsDataPointTypesEnum.DURATION,
    listAccessor: 'replies',
    color: PaletteColorsEnum.BLUE,
    metricRangeType: HistoMetricRangeTypesEnum.TIME,
  },
  {
    donutHistoMetricId: AnalyticsComputedGraphMetricsEnum.FIRST_REPLY_TIME_GRAPH,
    listHistoMetricId: AnalyticsTableMetricsEnum.FIRST_REPLY_TIME_HISTOGRAM,
    innerMetricId: AnalyticsScalarMetricsEnum.AVG_FIRST_REPLY_TIME,
    innerMetricType: AnalyticsDataPointTypesEnum.DURATION,
    listAccessor: 'replies',
    color: PaletteColorsEnum.BLUE,
    metricRangeType: HistoMetricRangeTypesEnum.TIME,
  },
];

// Pendo guide to be attached to the first analytics card.
const interactionId = 'analytics-metric-details';

enum ResolvedBarSubmetricsEnum {
  RESOLVED = 'resolved',
  UNRESOLVED = 'unresolved',
}

/*
 * Styles.
 */

const StyledLegendItemDiv = styled.div`
  ${flexCenter};
  gap: 8px;
`;

const StyledEditIntervalsButtonWrapperDiv = styled.div`
  ${flexCenter}
  margin-top: 4px;
  margin-left: -16px;
`;

/*
 * Props.
 */

interface OverviewReportProps extends BasicReportProps {
  bucketBoundaries?: AnalyticsOptionsBucketBoundaries;
}

/*
 * Component.
 */

export const OverviewReport: FC<OverviewReportProps> = ({isLoading, bucketBoundaries}) => {
  const [hoveredItemId, setHoveredItemId] = useState<string>();
  const dispatch = useAppDispatch();
  const localize = useLocalize();
  const trackAnalytics = useAnalyticsTracking();
  const {updateSelectedMetrics, maybeFetchHiddenMetrics} = useAnalyticsUpdateSelectedMetrics();
  const selectedMetrics = useAppSelector(getAnalyticsSelectedMetrics);
  const isSecondaryMetricLoading = useAppSelector(getAnalyticsSecondaryMetricIsLoading);
  const hasResolutionMetrics = useAppSelector(getHasAnalyticsResolutionMetrics);

  const [shouldShowHistogramExportDialog, toggleHistogramExportDialog] = useToggle();
  const [shouldShowTopTagsExportDialog, toggleTopTagsExportDialog] = useToggle();

  // The default view in the donut chart is "Reply Time".
  const defaultHistogramMetric = histogramMetrics[0];

  const [selectedHistogramMetric, setSelectedHistoGramMetric] =
    useState<HistoMetricSelection>(defaultHistogramMetric);

  const currentHistogramMetric = computeCurrentHistogramMetricSelection(histogramMetrics, selectedMetrics);
  // Previous values used for determining when our state has changed and we
  // need to fetch hidden metrics/metrics with different bucket boundaries.
  const previousHistogramMetric = usePrevious(currentHistogramMetric);

  /*
   * Lifecycle.
   */

  // This will trigger when we select a new view or change the selected metric.
  useEffect(() => {
    if (currentHistogramMetric) {
      if (!isEqual(currentHistogramMetric, previousHistogramMetric)) {
        setSelectedHistoGramMetric(currentHistogramMetric);
        maybeFetchHiddenMetrics(currentHistogramMetric.listHistoMetricId); // Will fetch the metrics if not already present.
      }
    } else if (selectedHistogramMetric.listHistoMetricId !== defaultHistogramMetric.listHistoMetricId) {
      setSelectedHistoGramMetric(defaultHistogramMetric); // If there is no current selection reset to our default.
    }
  }, [
    currentHistogramMetric,
    previousHistogramMetric,
    selectedHistogramMetric.listHistoMetricId,
    maybeFetchHiddenMetrics,
  ]);

  /*
   * Event Handlers.
   */

  const trackAndSetSelectedHistoMetric = (selection: HistoMetricSelection) => {
    trackAnalytics(InterfaceEventsEnum.ANALYTICS_METRIC_SELECT, {
      metric_type: selection.listHistoMetricId,
      report_section: ReportElementIdsEnum.OVERVIEW_REPLY_TIMES_WRAPPER,
    });

    setSelectedHistoGramMetric(selection);
    maybeFetchHiddenMetrics(selection.listHistoMetricId); // Will fetch the metrics if not already present.

    updateSelectedMetrics([selection.listHistoMetricId], ReportElementIdsEnum.OVERVIEW_REPLY_TIMES_WRAPPER);
  };

  const onProgessBarSerieSelect = (serieIndex: number) => {
    const isResolvedSerie = serieIndex === 1;
    dispatch(
      setMetricDetailsAction(
        AnalyticsScalarMetricsEnum.PCT_RESOLVED_SEGMENTS,
        {
          [ReportFilterOptionsEnum.COLLECT_CONTRIBUTING_SEGMENT_IDS]: {
            [ContributingSegmentsOptionsEnum.SUB_METRIC]: isResolvedSerie
              ? ResolvedBarSubmetricsEnum.RESOLVED
              : ResolvedBarSubmetricsEnum.UNRESOLVED,
          },
        },
        isResolvedSerie
          ? localize(
              defineMessage({
                id: 'metric-details-resolved-segments',
                message: 'Resolved conversations',
              }),
            )
          : localize(
              defineMessage({
                id: 'metric-details-unresolved-segments',
                message: 'Unresolved conversations',
              }),
            ),
      ),
    );
  };

  const onDataPointSelect = (
    dataPoint: AnalyticsGraphMetricValue,
    _serie: ChartSerie<AnalyticsGraphMetricValue>,
    selectedMetric?: AnyGraphMetricsEnum,
  ) => {
    // Adding a type guard but it shouldn't technically happen.
    if (!selectedMetric || isComputedGraphMetric(selectedMetric)) {
      return;
    }

    dispatch(
      setMetricDetailsAction(selectedMetric, {
        [ReportFilterOptionsEnum.COLLECT_CONTRIBUTING_SEGMENT_IDS]: {
          [ContributingSegmentsOptionsEnum.ROW]: dataPoint.start,
        },
      }),
    );
  };

  const onHistogramItemClick = ({label}: {label: ReactNode}, index: number) => {
    dispatch(
      setMetricDetailsAction(
        selectedHistogramMetric.listHistoMetricId,
        {
          [ReportFilterOptionsEnum.COLLECT_CONTRIBUTING_SEGMENT_IDS]: {
            [ContributingSegmentsOptionsEnum.SUB_METRIC]: selectedHistogramMetric.listAccessor,
            [ContributingSegmentsOptionsEnum.ROW]: index,
          },
          [ReportFilterOptionsEnum.BUCKET_BOUNDARIES]: bucketBoundaries,
        },
        `${getMetricLabels(selectedHistogramMetric.donutHistoMetricId).label}: ${label}`,
      ),
    );
  };

  const onDonutArcClick = (index: number, item?: AnalyticsDonutMetricValue) => {
    if (item) {
      onHistogramItemClick(item, index);
    }
  };

  /*
   * Component.
   */

  return (
    <>
      {/* Metrics Cards */}
      <ReportHeroCards
        reportType={AnalyticsReportsEnum.OVERVIEW}
        reportSectionId={ReportElementIdsEnum.OVERVIEW_METRICS_CARDS}
        isLoading={isLoading}
        interactionId={interactionId}
      />
      {/* Resolved conversations */}
      {hasResolutionMetrics && (
        <ReportProgressBar
          reportSectionId={ReportElementIdsEnum.OVERVIEW_RESOLVED_PCT}
          isLoading={isLoading}
          subtitle={
            <Trans id="overview-report-pct-resolve-subtitle">
              Only includes conversation segments that have been active during the selected time period.
              Activity includes messages, comments, tags, assignment.
            </Trans>
          }
          metric={AnalyticsComputedProgressMetricsEnum.RESOLVED_SEGMENTS_PROGRESS}
          onSerieSelect={onProgessBarSerieSelect}
          legendRenderers={progressBarLegendRenderers}
        />
      )}

      {/* Overview Performance Over Time Graph */}
      <ReportLineChart
        reportSectionId={ReportElementIdsEnum.OVERVIEW_CHANGES_OVER_TIME}
        title={localize(
          defineMessage({
            id: 'analytics-overview-report-changes-title',
            message: 'Changes Over Time',
          }),
        )}
        isLoading={isLoading}
        availableMetrics={[
          AnalyticsGraphMetricsEnum.MESSAGES_RECEIVED_GRAPH,
          AnalyticsGraphMetricsEnum.NUM_MESSAGES_SENT_GRAPH,
          AnalyticsGraphMetricsEnum.RESPONSE_GRAPH,
          AnalyticsGraphMetricsEnum.FIRST_RESPONSE_GRAPH,
          AnalyticsGraphMetricsEnum.HANDLE_TIME_GRAPH,
          AnalyticsGraphMetricsEnum.NEW_SEGMENTS_GRAPH,
          // Dynamically inserting resolution metrics if the FF is on.
          ...((hasResolutionMetrics && overviewGraphResolutionMetrics.map((metric) => metric.id)) || []),
          AnalyticsGraphMetricsEnum.ARCHIVED_SEGMENTS_GRAPH,
          AnalyticsGraphMetricsEnum.ARCHIVED_SEGMENTS_WITH_REPLY_GRAPH,
        ]}
        series={perfGraphSeries}
        xAccessor={(d: AnalyticsGraphMetricValue) => d.label}
        onDataPointSelect={onDataPointSelect}
      />

      {/* Reply times, Teammates and Tags lists */}
      <ReportElementWrapper
        reportSectionId={ReportElementIdsEnum.OVERVIEW_REPLY_TIMES_WRAPPER}
        // We always want the title of this section to be "Reply Time" regardless of which
        // metric is selected (since they are all views into reply times)
        title={<Trans id="overview-report-replis-section-title">Replies</Trans>}
        infoTooltip={getMetricLabels(selectedHistogramMetric.donutHistoMetricId).tooltip}
        width={4}
        renderCustom={() => (
          <HistoMetricSelectionDropdown
            metrics={histogramMetrics}
            setSelectedMetric={trackAndSetSelectedHistoMetric}
            selectedMetric={selectedHistogramMetric}
          />
        )}
        isExportable
        toggleExportDialog={toggleHistogramExportDialog}
      >
        <ReportDonutChart
          reportSectionId={ReportElementIdsEnum.OVERVIEW_REPLY_TIMES_DONUT}
          height={220}
          isLoading={isLoading || isSecondaryMetricLoading(selectedHistogramMetric.listHistoMetricId)}
          metric={selectedHistogramMetric.donutHistoMetricId}
          accessor={(d: AnalyticsGraphMetricValue) => d.value}
          reportListBindId={OverviewListsLabelAccessorsEnum.REPLY_TIME}
          hoveredItemId={hoveredItemId}
          onArcClick={onDonutArcClick}
          onArcHover={(index?: number) =>
            setHoveredItemId(makeHoveredItemId(OverviewListsLabelAccessorsEnum.REPLY_TIME, index))
          }
          color={selectedHistogramMetric.color}
          innerMetric={selectedHistogramMetric.innerMetricId}
          innerMetricType={selectedHistogramMetric.innerMetricType}
        />
        <ReportList
          reportSectionId={ReportElementIdsEnum.OVERVIEW_REPLY_TIMES_HISTOGRAM}
          // No need for an empty or error state since there is one in the donut chart
          renderEmptyState={() => null}
          renderErrorState={() => null}
          isLoading={isLoading || isSecondaryMetricLoading(selectedHistogramMetric.listHistoMetricId)}
          metric={selectedHistogramMetric.listHistoMetricId}
          labelAccessor={OverviewListsLabelAccessorsEnum.REPLY_TIME}
          valueAccessors={[selectedHistogramMetric.listAccessor]}
          hoveredItemId={hoveredItemId}
          labelCircleColor={selectedHistogramMetric.color}
          onItemClick={onHistogramItemClick}
          onItemHover={(index?: ReactText) =>
            setHoveredItemId(makeHoveredItemId(OverviewListsLabelAccessorsEnum.REPLY_TIME, index))
          }
          exportTitle={makeExportTitle(selectedHistogramMetric.donutHistoMetricId)}
          shouldShowExportDialog={shouldShowHistogramExportDialog}
          toggleExportDialog={toggleHistogramExportDialog}
        />
        <StyledEditIntervalsButtonWrapperDiv>
          <PopoverCoordinator
            renderButton={(isPopoverOpen) => (
              <Button variant={ButtonVariantsEnum.LINK} hasHover={isPopoverOpen}>
                <Trans id="overview-report-edit-intervals-button-label">Edit intervals</Trans>
              </Button>
            )}
            renderModal={(popoverProps: PopoverTransferProps) => (
              <HistoSettingsBucketsPicker
                {...popoverProps}
                metric={selectedHistogramMetric}
                bucketBoundaries={bucketBoundaries}
                color={selectedHistogramMetric.color}
              />
            )}
          />
        </StyledEditIntervalsButtonWrapperDiv>
      </ReportElementWrapper>

      {/* Team changes list */}
      <ReportList
        reportSectionId={ReportElementIdsEnum.OVERVIEW_TEAM_CHANGES_HISTOGRAM}
        title={getMetricLabels(AnalyticsTableMetricsEnum.TEAM_CHANGES).label}
        infoTooltip={getMetricLabels(AnalyticsTableMetricsEnum.TEAM_CHANGES).tooltip}
        width={4}
        isLoading={isLoading}
        metric={AnalyticsTableMetricsEnum.TEAM_CHANGES}
        labelAccessor={OverviewListsLabelAccessorsEnum.TEAMMATE}
        valueAccessors={[
          'messages_sent',
          'average_reply_time',
          'average_first_reply_time',
          'average_handle_time',
          'archived_segments',
          'archived_segments_with_reply',
        ]}
        shouldDisplayTops
        rowFilter={topListsFilter}
        hoveredItemId={hoveredItemId}
        onItemHover={(index?: ReactText) =>
          setHoveredItemId(makeHoveredItemId(OverviewListsLabelAccessorsEnum.TEAMMATE, index))
        }
      />

      {/* Top tags list */}
      <ReportList
        reportSectionId={ReportElementIdsEnum.OVERVIEW_TAGS_HISTOGRAM}
        title={getMetricLabels(AnalyticsTableMetricsEnum.TOP_TAGS).label}
        infoTooltip={getMetricLabels(AnalyticsTableMetricsEnum.TOP_TAGS).tooltip}
        width={4}
        isLoading={isLoading}
        metric={AnalyticsTableMetricsEnum.TOP_TAGS}
        labelAccessor={OverviewListsLabelAccessorsEnum.TAGS}
        valueAccessors={['active_conversations']}
        hoveredItemId={hoveredItemId}
        onItemHover={(index?: ReactText) =>
          setHoveredItemId(makeHoveredItemId(OverviewListsLabelAccessorsEnum.TAGS, index))
        }
        isExportable
        shouldShowExportDialog={shouldShowTopTagsExportDialog}
        toggleExportDialog={toggleTopTagsExportDialog}
      />
    </>
  );
};

/*
 * Helpers.
 */

function makeHoveredItemId(listId: OverviewListsLabelAccessorsEnum, itemIndex?: ReactText) {
  return `${listId}-${itemIndex}`;
}

const computeCurrentHistogramMetricSelection = (
  histoMetrics: ReadonlyArray<HistoMetricSelection>,
  selectedMetrics: AnalyticsSelectedMetricsModel,
) => {
  if (!selectedMetrics[ReportElementIdsEnum.OVERVIEW_REPLY_TIMES_WRAPPER]) {
    return undefined;
  }

  // The selected metrics interface is a little confusing, but the important part for this code block is that
  // for the reply times section we only persist the listHistoMetricId, and it is always the first
  // element of that string array.
  return histoMetrics.find(
    (metric) =>
      metric.listHistoMetricId === selectedMetrics[ReportElementIdsEnum.OVERVIEW_REPLY_TIMES_WRAPPER][0],
  );
};
